import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { NotificationBell } from "../components/NotificationBell";
import { WakeWordListener } from "../components/WakeWordListener";
import { HelpInfo } from "../components/HelpInfo";
import { InstallPWA } from "../components/InstallPWA";
import { PushPrompt } from "../components/PushPrompt";
import { IosNotifyReminder } from "../components/IosNotifyReminder";
import { TitolareBanner } from "../components/TitolareBanner";
import {
  LayoutDashboard, ClipboardList, PlusCircle, Building2, Wrench, FileBarChart, LogOut, ShieldAlert, ShieldCheck, Users, CalendarDays, BarChart3, FileText, Landmark, Megaphone, PlayCircle, SlidersHorizontal, Clock, Sparkles, MessageSquare, BookOpen, FolderArchive, HardHat, Handshake, Scale, UploadCloud, Ticket, Wallet, MapPin, UserCircle, Palette,
} from "lucide-react";

const ROLE_LABEL = { admin: "Studio / Amministratore", condomino: "Condomino", fornitore: "Fornitore" };
const EMBLEM = "/ginius-emblem.png";

function TrialBanner() {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/me/trial").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  if (!info?.trial) return null;
  const giorni = info.giorni_residui;
  const urgente = giorni !== null && giorni <= 5;
  return (
    <div data-testid="trial-banner"
      className={`mb-6 rounded-xl border-2 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 ${urgente ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
      <span className="flex items-center gap-2 font-bold"><Clock className="w-5 h-5" /> Versione di prova</span>
      <span className="text-sm">
        {giorni !== null ? <>Giorni rimanenti: <b data-testid="trial-giorni">{giorni}</b></> : "Periodo di prova attivo"}
      </span>
      <span className="text-sm opacity-80">
        Limiti: <b>{info.uso?.condomini || 0}</b> palazzine (<b>{Math.min(1, info.uso?.condomini || 0)}</b> attiva · <b>{Math.max(0, (info.uso?.condomini || 0) - 1)}</b> da attivare) · <b>{info.uso?.fornitori || 0}</b>/<b>{info.limiti?.fornitori}</b> fornitori
      </span>
      <a data-testid="trial-upgrade-cta"
        href="/piani"
        className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-brand text-white text-sm font-bold px-4 py-1.5 hover:bg-brand-dark transition-colors">
        <Sparkles className="w-4 h-4" /> Passa ad un Piano
      </a>
    </div>
  );
}

export function Layout({ children }) {
  const { user, branding, logout } = useAuth();
  const isCustomBrand = !!branding?.custom;
  // Iter122dc · Il tagline "by Amministrazioni Izzo" è brand identity dello Studio Izzo,
  // non del prodotto Ginius. Mostralo SOLO al tenant Izzo (tenant_id === "studio").
  // Per gli altri tenant senza branding custom mostriamo il logo Ginius con il punto d'oro,
  // senza attribuzione a Izzo (richiesta utente white-label multitenant).
  const isStudioIzzo = user?.tenant_id === "studio";
  const brandLogo = branding?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : EMBLEM;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    import("../lib/push").then((m) => m.ensurePushFresh()).catch(() => {});
  }, [user]);

  const [hasAppalti, setHasAppalti] = useState(false);
  useEffect(() => {
    if (user?.role === "fornitore" || user?.role === "condomino") {
      api.get("/appalti").then((r) => setHasAppalti((r.data || []).length > 0)).catch(() => setHasAppalti(false));
    }
  }, [user?.role]);

  // Iter122at · badge sidebar: per il condòmino include Bacheca (avvisi + moduli), Manutenzioni, Sinistri
  const [unread, setUnread] = useState({ preventivi: 0, manutenzioni: 0, credius: 0, bacheca: 0, sinistri: 0, documenti: 0, raccolte: 0, contabilita_avv: 0 });
  // Iter122au · toast di benvenuto al primo fetch se ci sono novità per il condòmino
  const welcomeToastShownRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    // Iter122at · include condòmini (badge bacheca/manutenzioni/sinistri)
    if (!["admin", "fornitore", "credius_client", "condomino"].includes(user.role)) return;
    let alive = true;
    const fetchUnread = () => api.get("/me/messaggi-non-letti").then((r) => {
      if (!alive) return;
      setUnread(r.data);
      // Toast di benvenuto (solo condòmino, una volta per sessione)
      if (user.role === "condomino" && !welcomeToastShownRef.current) {
        welcomeToastShownRef.current = true;
        const parts = [];
        if (r.data.bacheca) parts.push(`${r.data.bacheca} nuovi in Bacheca`);
        if (r.data.manutenzioni) parts.push(`${r.data.manutenzioni} manutenzioni aggiornate`);
        if (r.data.sinistri) parts.push(`${r.data.sinistri} sinistri aggiornati`);
        if (r.data.documenti) parts.push(`${r.data.documenti} nuovi documenti`);
        if (r.data.raccolte) parts.push(`${r.data.raccolte} sondaggi da votare`);
        if (parts.length) {
          setTimeout(() => toast.message("Ciao! Hai novità", { description: parts.join(" · "), duration: 6000 }), 800);
        }
      }
    }).catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [user, location.pathname]);
  const unreadFor = (to) => {
    if (to === "/preventivi") return unread.preventivi || 0;
    if (to === "/credius") return unread.credius || 0;
    // Iter122at · Condòmino: badge su Bacheca, Manutenzioni ("/segnalazioni") e Sinistri ("/miei-sinistri")
    if (user?.role === "condomino") {
      if (to === "/bacheca") return unread.bacheca || 0;
      if (to === "/segnalazioni") return unread.manutenzioni || 0;
      if (to === "/miei-sinistri") return unread.sinistri || 0;
      if (to === "/documenti") return unread.documenti || 0;
      if (to === "/mie-raccolte") return unread.raccolte || 0;
      return 0;
    }
    // Admin/fornitore: badge manutenzioni globale + sinistri (Iter122k98)
    if (to === "/segnalazioni") return unread.manutenzioni || 0;
    if (to === "/sinistri") return unread.sinistri || 0;
    // Iter123k190 · Admin: badge "Bacheca" per nuove compilazioni moduli dei condòmini
    if (to === "/bacheca") return unread.bacheca || 0;
    // Iter122k123 · Avvocato: badge sulla nuova voce Contabilità (notule Pagate non ancora aperte)
    if (to === "/credius/contabilita-avvocato") return unread.contabilita_avv || 0;
    return 0;
  };

  const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin"], planFeature: "dashboard" },
    { to: "/nuova", label: "Nuova Manutenzione", labels: { condomino: "Segnala una manutenzione" }, icon: PlusCircle, roles: ["admin", "condomino"], planFeature: "nuova_manutenzione" },
    { to: "/segnalazioni", label: "Manutenzioni", labels: { condomino: "Le mie manutenzioni" }, icon: Ticket, roles: ["admin", "condomino", "fornitore"], planFeature: "manutenzioni" },
    { to: "/condomini", label: "Stabili", icon: Building2, roles: ["admin"], planFeature: "stabili" },
    { to: "/proprieta", label: "Anagrafica Condòmini", labels: { condomino: "I dati del mio immobile" }, icon: Landmark, roles: ["admin", "condomino"], planFeature: "anagrafica" },
    { to: "/residenti", label: "Account Condòmini", icon: Users, roles: ["admin"], planFeature: "account_condomini" },
    { to: "/bacheca", label: "Bacheca", labels: { condomino: "Bacheca avvisi" }, icon: Megaphone, roles: ["admin", "condomino"], planFeature: "bacheca" },
    { to: "/fornitori", label: "Account Fornitori", icon: Wrench, roles: ["admin"], planFeature: "account_fornitori" },
    { to: "/comunicazioni-fornitori", label: "Comunicazioni fornitori", labels: { fornitore: "Comunicazioni dallo Studio" }, icon: MessageSquare, roles: ["admin", "fornitore"], planFeature: "comunicazioni_fornitori" },
    { to: "/documenti-richiesti", label: "Documenti richiesti", icon: FolderArchive, roles: ["fornitore"] },
    { to: "/documenti", label: "Documenti condominio", icon: FolderArchive, roles: ["condomino"] },
    { to: "/raccolta-dati", label: "Raccolta dati", icon: ClipboardList, roles: ["admin"], studioOnly: true, trialSection: "raccolta_dati", tenantSection: "raccolta_dati", planFeature: "raccolta_dati" },
    { to: "/mie-raccolte", label: "I miei sondaggi", icon: ClipboardList, roles: ["condomino"] },
    { to: "/preventivi", label: "Preventivi", icon: FileText, roles: ["admin", "fornitore"], studioOnly: true, trialSection: "preventivi", tenantSection: "preventivi", planFeature: "preventivi" },
    { to: "/credius", label: "Credius · Legale", labels: { condomino: "Credius · Le mie pratiche", fornitore: "Credius · Legale", credius_client: "Le mie pratiche" }, icon: Scale, roles: ["admin", "fornitore", "condomino", "credius_client"], studioOnly: true, tenantSection: "credius", planFeature: "credius" },
    { to: "/credius/anagrafica", label: "Credius · Anagrafiche", icon: Users, roles: ["admin"], studioOnly: true, tenantSection: "credius", planFeature: "credius_anagrafica" },
    { to: "/sinistri", label: "Sinistri", icon: ShieldAlert, roles: ["admin", "fornitore"], studioOnly: true, trialSection: "sinistri", tenantSection: "sinistri", planFeature: "sinistri" },
    // Iter122k72 · Sezione "Le mie convenzioni" visibile solo ai fornitori convenzionati (has_credius_convention)
    // Iter122k92 · Icona Handshake (era Scale, sovrapposta a Credius · Legale nella sidebar)
    { to: "/mie-convenzioni", label: "Le mie convenzioni", icon: Handshake, roles: ["fornitore"], needsCredius: true },
    { to: "/privacy", label: "Privacy", icon: ShieldCheck, roles: ["admin"], studioOnly: true, planFeature: "privacy" },
    { to: "/ras", label: "RAS / Fascicolo", icon: FolderArchive, roles: ["admin"], studioOnly: true, trialSection: "ras", tenantSection: "ras", planFeature: "ras" },
    { to: "/appalti", label: "Gestione Appalti", icon: HardHat, roles: ["admin"], studioOnly: true, trialSection: "appalti", tenantSection: "appalti", planFeature: "appalti" },
    { to: "/copilot/mappa", label: "Mappa Sopralluoghi", icon: MapPin, roles: ["admin"], studioOnly: true, trialSection: "appalti", tenantSection: "appalti", planFeature: "appalti" },
    { to: "/copilot/statistiche", label: "Statistiche CoPilot", icon: BarChart3, roles: ["admin"], studioOnly: true, trialSection: "appalti", tenantSection: "appalti", planFeature: "appalti" },
    { to: "/copilot/profilo", label: "Profilo Ginius", icon: UserCircle, roles: ["admin"], studioOnly: true, trialSection: "appalti", tenantSection: "appalti", planFeature: "appalti" },
    { to: "/appalti", label: "I miei appalti", icon: HardHat, roles: ["fornitore", "condomino"], needsAppalti: true, tenantSection: "appalti" },
    { to: "/miei-sinistri", label: "Sinistri", labels: { condomino: "I miei sinistri" }, icon: ShieldAlert, roles: ["condomino"], tenantSection: "sinistri" },
    { to: "/scadenze", label: "Scadenze", icon: CalendarDays, roles: ["admin"], planFeature: "calendario" },
    { to: "/statistiche", label: "Statistiche", icon: BarChart3, roles: ["admin"], studioOnly: true, trialSection: "statistiche", tenantSection: "statistiche", planFeature: "statistiche" },
    { to: "/report", label: "Report", icon: FileBarChart, roles: ["admin"], studioOnly: true, trialSection: "report", tenantSection: "report", planFeature: "report" },
    { to: "/pannello", label: "Impostazioni & Account", icon: SlidersHorizontal, roles: ["admin"], studioOnly: true, planFeature: "impostazioni_account" },
    // Iter123k245 · Branding Studio accessibile anche ai trial (fuori da /pannello che è studioOnly)
    { to: "/branding-studio", label: "Branding Studio", icon: Palette, roles: ["admin"] },
    // Iter122dv · Rotta /impostazioni ora ridirige a /pannello?tab=profilo-studio (backward compat)
    // Iter122cd · SuperAdmin visibile solo al ruolo super_admin (rimosso accesso admin Izzo)
    { to: "/superadmin", label: "SuperAdmin", icon: ShieldCheck, roles: ["super_admin"] },
    { to: "/import", label: "Importa dati", icon: UploadCloud, roles: ["admin"], studioOnly: true, planFeature: "importa_dati" },
    { to: "/guida", label: "Guide e manuali", icon: BookOpen, roles: ["admin", "condomino", "fornitore", "credius_client"], planFeature: "guida" },
    // Iter122k122 · Contabilità Avvocato — pro-forma pagate + fatture elettroniche allegate.
    // Visibile solo al fornitore convenzionato avvocato (is_avvocato=true).
    { to: "/credius/contabilita-avvocato", label: "Contabilità", icon: Wallet, roles: ["fornitore"], needsAvvocato: true, planFeature: "credius" },
  ].filter((n) => n.roles.includes(user?.role))
    .filter((n) => !n.needsAppalti || hasAppalti)
    // Iter122k72 · "Le mie convenzioni" solo per fornitori con convenzione attiva
    .filter((n) => !n.needsCredius || user?.has_credius_convention)
    // Iter122k122 · "Contabilità" solo per fornitori avvocati convenzionati
    .filter((n) => !n.needsAvvocato || user?.is_avvocato)
    // Iter122k72 · Nasconde Sinistri al fornitore convenzionato che NON ha sinistri assegnati
    .filter((n) => !(n.to === "/sinistri" && user?.role === "fornitore" && user?.hide_sinistri))
    // Credius: per i fornitori è visibile solo agli avvocati/studi legali (SOSTITUISCE Manutenzioni),
    // OPPURE ai fornitori/condòmini che risultano clienti di almeno una pratica (Fase C3b, vista in sola lettura).
    .filter((n) => !(n.to === "/credius" && user?.role === "fornitore" && !user?.is_avvocato && !user?.is_credius_client))
    .filter((n) => !(n.to === "/credius" && user?.role === "condomino" && !user?.is_credius_client))
    .filter((n) => !(n.to === "/segnalazioni" && user?.role === "fornitore" && user?.is_avvocato))
    .filter((n) => {
      // Iter122ed · Trial gate legacy (`trial_sezioni`) disattivato quando plan_features è presente
      // (fonte di verità unica ora è la matrice Excel via plan_features).
      const hasPlanFeatures = Array.isArray(user?.plan_features) && user.plan_features.length > 0;
      if (hasPlanFeatures) return true;
      if (!(n.studioOnly && user?.account_type === "trial")) return true;
      return n.trialSection && (user?.trial_sezioni || []).includes(n.trialSection);
    })
    // Sezioni opzionali disattivate dal Super Admin per lo studio (tenant reale)
    .filter((n) => !(n.tenantSection && (user?.sezioni_disabilitate || []).includes(n.tenantSection)))
    // Iter122bb · filtro Izzo-only per la sezione SuperAdmin nel menu
    .filter((n) => !n.izzoOnly || user?.tenant_id === "studio")
    // Iter122eb · Gating per piano condominio (matrice "Piani e funzioni GiniusAPP")
    // Skip per super_admin, Izzo studio (tenant_id="studio") e voci senza planFeature (ruoli non-admin).
    .filter((n) => {
      if (!n.planFeature) return true;
      if (user?.role === "super_admin" || user?.tenant_id === "studio") return true;
      const feats = user?.plan_features || [];
      return feats.length === 0 || feats.includes(n.planFeature);
    })
    // Iter122ii · Restrizione menu per fornitori Assicuratori / Peritali / Liquidatori.
    // - Assicuratori/Assicurazioni: solo Comunicazioni, Documenti richiesti, Sinistri, Preventivi
    // - Periti/Studi peritali/Liquidatori: solo Sinistri
    .filter((n) => {
      if (user?.role !== "fornitore") return true;
      const cats = (user?.fornitore_categorie || []).map((c) => (c || "").toLowerCase());
      const has = (needles) => cats.some((c) => needles.some((k) => c.includes(k)));
      const isPeritale = has(["perito", "peritale", "liquidatore"]);
      const isAssicur = has(["assicur"]);
      if (isPeritale && !isAssicur) {
        // Solo Sinistri visibile
        return n.to === "/sinistri";
      }
      if (isAssicur) {
        // Solo Comunicazioni · Documenti richiesti · Sinistri · Preventivi
        return ["/comunicazioni-fornitori", "/documenti-richiesti", "/sinistri", "/preventivi"].includes(n.to);
      }
      return true;
    })
    .map((n) => ({ ...n, label: (n.labels && n.labels[user?.role]) || n.label }));

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-72 bg-[#1A1818] text-white flex flex-col md:min-h-screen md:fixed md:inset-y-0 sticky top-0 z-40 md:z-auto">
        {/* Intestazione mobile (sticky in cima insieme alla navigazione) */}
        <div className="md:hidden flex justify-between items-center gap-2 px-3 py-2 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <span className="ginius-flip-wrap inline-block w-9 h-9 rounded-lg shrink-0" style={{ perspective: "600px" }}>
              <img src={brandLogo} alt={branding?.nome || "Ginius"} className="w-9 h-9 rounded-lg object-contain" />
              <span data-back className="rounded-lg text-2xl" aria-hidden="true">G</span>
            </span>
            <div className="leading-none">
              {isCustomBrand ? (
                <div className="font-brand font-bold text-sm text-white leading-tight" data-testid="brand-name-mobile">{branding.nome}</div>
              ) : (
                <>
                  <div className="font-brand font-bold text-lg text-brand">Ginius</div>
                  {isStudioIzzo && (
                    <div className="text-[10px] text-slate-300" data-testid="brand-tagline-izzo-mobile">by Amministrazioni Izzo</div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <InstallPWA variant="outline" className="h-8 px-2 text-xs" />
            <HelpInfo variant="dark" />
            <NotificationBell variant="dark" />
            <Button data-testid="logout-button-mobile" onClick={() => { logout(); navigate("/login"); }} variant="outline" size="sm" className="bg-slate-700 hover:bg-slate-600 text-white border-0">
              <LogOut className="w-4 h-4 mr-1" /> Esci
            </Button>
          </div>
        </div>
        <div className="p-6 border-b border-slate-700 hidden md:block bg-gradient-to-b from-[#2A0E1A] to-transparent">
          <div className="flex items-center gap-4 smoke-in">
            <span className="ginius-flip-wrap inline-block w-16 h-16 rounded-2xl shrink-0 ring-2 ring-[#D4AF5A]/70 shadow-[0_0_26px_rgba(212,175,90,0.45)]" style={{ perspective: "600px" }}>
              <img src={brandLogo} alt={branding?.nome || "Ginius"} className="w-16 h-16 rounded-2xl object-contain" />
              <span data-back className="rounded-2xl text-4xl" aria-hidden="true">G</span>
            </span>
            <div className="min-w-0">
              {isCustomBrand ? (
                <div className="font-brand font-bold text-xl leading-tight text-white" data-testid="brand-name-desktop">{branding.nome}</div>
              ) : (
                <>
                  <div className="font-brand font-bold text-4xl leading-none text-brand drop-shadow-[0_0_10px_rgba(255,255,255,0.35)]" style={{ WebkitTextStroke: "0.6px #FFFFFF" }} data-testid="brand-name-desktop">
                    Ginius<span className="text-[#D4AF5A]" style={{ WebkitTextStroke: 0 }}>.</span>
                  </div>
                  {isStudioIzzo && (
                    <div className="text-xs text-[#E7C9A0] font-semibold tracking-wide mt-1.5" data-testid="brand-tagline-izzo-desktop">by Amministrazioni Izzo</div>
                  )}
                </>
              )}
            </div>
          </div>
          {(isCustomBrand ? branding.slogan : "Tu lo chiedi, lui lo fa!") && (
            <div className="text-sm text-[#E7B7C6] italic mt-3 font-medium">{isCustomBrand ? branding.slogan : "Tu lo chiedi, lui lo fa!"}</div>
          )}
        </div>
        <nav className="flex md:flex-col gap-1 p-3 md:p-4 w-full overflow-x-auto">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-semibold whitespace-nowrap transition-colors ${
                  isActive(n.to) ? "bg-brand text-white" : "text-slate-300 hover:bg-slate-800"
                }`}
              >
                <Icon className="w-6 h-6 shrink-0" />
                <span className="hidden md:inline">{n.label}</span>
                {unreadFor(n.to) > 0 && (
                  <span data-testid={`nav-badge-${n.to.slice(1)}`} className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-red-600 text-white shrink-0">{unreadFor(n.to)}</span>
                )}
              </Link>
            );
          })}
          <Link
            to="/presentazione"
            data-testid="nav-presentazione"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-bold whitespace-nowrap transition-colors text-[#1A1818] bg-[#D4AF5A] hover:bg-[#c39f48] md:mt-2"
          >
            <PlayCircle className="w-6 h-6 shrink-0" />
            <span className="hidden md:inline">Presentazione</span>
          </Link>
        </nav>
        <div className="hidden md:block mt-auto p-4 border-t border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-slate-400">{ROLE_LABEL[user?.role]}</div>
            </div>
            <div className="flex items-center gap-1">
              <HelpInfo variant="dark" />
              <NotificationBell variant="dark" />
            </div>
          </div>
          <Button
            data-testid="logout-button"
            onClick={() => { logout(); navigate("/login"); }}
            variant="secondary"
            className="w-full h-11 bg-slate-700 hover:bg-slate-600 text-white border-0"
          >
            <LogOut className="w-5 h-5 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 min-w-0 md:ml-72 p-3 sm:p-4 lg:p-6 pb-24 sm:pb-24 lg:pb-28 fade-in overflow-x-hidden ${location.pathname === "/" ? "dashboard-bg" : ""}`}>
        <TrialBanner />
        {/* Iter122eh · EmailPolicyBanner rimosso dal Layout globale; ora visibile solo in Impostazioni & Account → Preferenze notifiche via email */}
        <TitolareBanner />
        {(user?.role === "condomino" || user?.role === "fornitore") && <PushPrompt />}
        {children}
      </main>
      <IosNotifyReminder />
      <WakeWordListener />
    </div>
  );
}
